import { useId, useState, type ClipboardEvent, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { addressSaveError, type AddressInput } from "@/hooks/useCompanyAddresses";
import { parseAddressText, validateAddress, VERIFICATION_LABEL, type Address, type AddressKind, type Verification } from "@shared/address";
import { US_STATES, stateCode } from "@shared/usStates";

/** What the inputs hold. `verification` is blank until someone picks one. */
type Draft = Omit<Required<AddressInput>, "verification"> & { verification: Verification | "" };
type FieldError = { field: string | null; message: string };

const VERIFICATIONS = Object.keys(VERIFICATION_LABEL) as Verification[];
const STATE_CODES = Object.keys(US_STATES).sort((a, b) => US_STATES[a].localeCompare(US_STATES[b]));
const COUNTRY_MESSAGE = "Country must be a two-letter code, like US or CA.";

const INPUT = "h-7 px-2 text-[12.5px] md:text-[12.5px]";
const LABEL = "text-[11px] font-medium text-muted-foreground";

function draftFrom(value: Address | null, defaultAddressee: string | null): Draft {
  return {
    street: value?.street ?? "",
    line2: value?.line2 ?? "",
    city: value?.city ?? "",
    state: value?.state ?? "",
    postalCode: value?.postalCode ?? "",
    country: value?.country ?? "US",
    addressee: value?.addressee || defaultAddressee || "",
    verification: value?.verification ?? "",
    verificationNote: value?.verificationNote ?? "",
    verifiedOn: value?.verifiedOn ?? "",
  };
}

const PARTS = ["street", "line2", "city", "state", "postalCode", "country"] as const;

/** True when street, suite, city, state, ZIP or country differs from the stored address. */
export function addressPartsChanged(d: Pick<Draft, (typeof PARTS)[number]>, stored: Address | null): boolean {
  if (!stored) return false;
  return PARTS.some((k) => d[k].trim() !== (stored[k] ?? "").trim());
}

/**
 * An edit that moves the address to a different house drops the stored
 * confirmation and date, so "How confirmed" has to be chosen again. Only the
 * edit that first changes a part resets; a choice made after that is kept.
 */
export function clearStaleVerification(prev: Draft, next: Draft, stored: Address | null): Draft {
  return !addressPartsChanged(prev, stored) && addressPartsChanged(next, stored)
    ? { ...next, verification: "", verifiedOn: "" }
    : next;
}

/** The body for this kind: a company address carries no home details. */
function inputFrom(d: Draft, kind: AddressKind, outsideUS: boolean): AddressInput {
  const parts = {
    street: d.street, line2: d.line2, city: d.city, state: d.state, postalCode: d.postalCode,
    country: outsideUS ? d.country : "US",
  };
  if (kind === "company") return parts;
  return {
    ...parts,
    addressee: d.addressee,
    ...(d.verification ? { verification: d.verification } : {}),
    verificationNote: d.verificationNote,
    verifiedOn: d.verifiedOn,
  };
}

/**
 * Add, edit or remove one address in a popover whose trigger is `children`.
 * Street takes a whole pasted address and splits it. Messages come from the
 * same validateAddress the API runs, and a 400 lands under the field it names.
 */
export function AddressEditor({ kind, value, defaultAddressee, onSave, onRemove, children }: {
  kind: AddressKind;
  value: Address | null;
  defaultAddressee: string | null;
  onSave: (input: AddressInput) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
  children: ReactNode;
}) {
  const isHome = kind === "owner_home";
  const uid = useId();
  const idFor = (name: keyof Draft) => `${uid}-${name}`;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(value, defaultAddressee));
  const [outsideUS, setOutsideUS] = useState(false);
  const [error, setError] = useState<FieldError | null>(null);
  const [removing, setRemoving] = useState(false);
  const [busy, setBusy] = useState(false);

  const onOpenChange = (next: boolean) => {
    if (busy) return;
    setOpen(next);
    if (next) {
      setDraft(draftFrom(value, defaultAddressee));
      setOutsideUS(!!value && value.country !== "US");
      setError(null);
      setRemoving(false);
    }
  };

  const set = (k: keyof Draft) => (v: string) => {
    setDraft((d) => clearStaleVerification(d, { ...d, [k]: v }, value));
    if (error?.field === k) setError(null);
  };

  const onStreetPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    // A copied mailing label arrives on several lines; parseAddressText reads commas.
    const parsed = parseAddressText(e.clipboardData.getData("text").replace(/\s*\n\s*/g, ", "));
    if (!parsed) return;
    e.preventDefault();
    setDraft((d) => clearStaleVerification(d, {
      ...d,
      street: parsed.street, line2: parsed.line2 ?? "", city: parsed.city,
      state: parsed.state ?? "", postalCode: parsed.postalCode ?? "", country: "US",
    }, value));
    setOutsideUS(false);
    setError(null);
  };

  const toggleOutsideUS = () => {
    // Leaving the US clears the implied "US" so Country starts blank. Coming back,
    // a typed "Texas" becomes TX for the state list and anything else clears.
    setDraft((d) => clearStaleVerification(d, outsideUS
      ? { ...d, state: stateCode(d.state) ?? "", country: "US" }
      : { ...d, country: d.country === "US" ? "" : d.country }, value));
    setOutsideUS(!outsideUS);
    setError(null);
  };

  const save = async () => {
    const input = inputFrom(draft, kind, outsideUS);
    // A blank country reads as US, which would put the error under State.
    if (outsideUS && !draft.country.trim()) { setError({ field: "country", message: COUNTRY_MESSAGE }); return; }
    const check = validateAddress(input, kind);
    if (!check.ok) { setError({ field: check.field, message: check.message }); return; }
    setBusy(true);
    try {
      await onSave(input);
      setOpen(false);
      toast.success(isHome ? "Home address saved" : "Company address saved");
    } catch (e) {
      setError(addressSaveError(e, "Couldn't save the address. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await onRemove();
      setOpen(false);
      toast.success(isHome ? "Home address removed" : "Company address removed");
    } catch (e) {
      setError(addressSaveError(e, "Couldn't remove the address. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const shown = ["street", "line2", "city", "state", "postalCode",
    ...(outsideUS ? ["country"] : []),
    ...(isHome ? ["addressee", "verification", "verifiedOn", "verificationNote"] : [])];
  const messageFor = (field: string) =>
    error && error.field === field ? <p role="alert" className="text-[11px] text-destructive">{error.message}</p> : null;
  const general = error && !(error.field && shown.includes(error.field))
    ? <p role="alert" className="text-[11px] text-destructive">{error.message}</p>
    : null;

  const field = (name: keyof Draft, label: string, input: ReactNode) => (
    <div className="grid gap-1">
      <label htmlFor={idFor(name)} className={LABEL}>{label}</label>
      {input}
      {messageFor(name)}
    </div>
  );
  const text = (name: keyof Draft, label: string, props: { type?: string; autoComplete?: string; onPaste?: (e: ClipboardEvent<HTMLInputElement>) => void } = {}) =>
    field(name, label, (
      <Input
        id={idFor(name)}
        className={INPUT}
        value={draft[name]}
        onChange={(e) => set(name)(e.target.value)}
        aria-invalid={error?.field === name || undefined}
        disabled={busy}
        {...props}
      />
    ));

  const title = isHome ? "Owner's home" : "Company address";

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" collisionPadding={8} className="max-h-[var(--radix-popover-content-available-height)] w-80 overflow-y-auto p-3 text-[12.5px]">
        {removing ? (
          <div className="grid gap-2.5">
            <p className="text-[13px] font-semibold">Remove this address?</p>
            {general}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" className="h-7 min-h-0" disabled={busy} onClick={() => setRemoving(false)}>Cancel</Button>
              <Button type="button" variant="destructive" size="sm" className="h-7 min-h-0" disabled={busy} onClick={remove}>
                {busy && <Loader2 className="animate-spin" />}Remove
              </Button>
            </div>
          </div>
        ) : (
          <form className="grid gap-2" onSubmit={(e) => { e.preventDefault(); void save(); }}>
            <p className="text-[13px] font-semibold">{title}</p>
            {isHome && text("addressee", "Addressee", { autoComplete: "off" })}
            {text("street", "Street", { autoComplete: "off", onPaste: onStreetPaste })}
            {text("line2", "Suite / unit", { autoComplete: "off" })}
            {text("city", "City", { autoComplete: "off" })}
            <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-2">
              {outsideUS
                ? text("state", "State or region", { autoComplete: "off" })
                : field("state", "State", (
                  <Select value={draft.state} onValueChange={set("state")} disabled={busy}>
                    <SelectTrigger id={idFor("state")} className="h-7 px-2 py-0 text-[12.5px]" aria-invalid={error?.field === "state" || undefined}>
                      <SelectValue placeholder="Pick a state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {STATE_CODES.map((code) => <SelectItem key={code} value={code} className="text-[12.5px]">{US_STATES[code]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ))}
              {text("postalCode", outsideUS ? "Postal code" : "ZIP", { autoComplete: "off" })}
            </div>
            {outsideUS && text("country", "Country (two-letter code)", { autoComplete: "off" })}
            <button
              type="button"
              onClick={toggleOutsideUS}
              className="justify-self-start text-[11px] font-semibold text-primary-text hover:underline"
            >
              {outsideUS ? "In the US?" : "Outside the US?"}
            </button>

            {isHome && (
              <>
                {field("verification", "How confirmed", (
                  <Select value={draft.verification} onValueChange={set("verification")} disabled={busy}>
                    <SelectTrigger id={idFor("verification")} className="h-7 px-2 py-0 text-[12.5px]" aria-invalid={error?.field === "verification" || undefined}>
                      <SelectValue placeholder="Choose one" />
                    </SelectTrigger>
                    <SelectContent>
                      {VERIFICATIONS.map((v) => <SelectItem key={v} value={v} className="text-[12.5px]">{VERIFICATION_LABEL[v]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ))}
                {!draft.verification && error?.field !== "verification" && addressPartsChanged(draft, value) && (
                  <p className="-mt-1 text-[11px] text-muted-foreground">Address changed. Say how the new one was confirmed.</p>
                )}
                {text("verifiedOn", "Date checked", { type: "date" })}
                {field("verificationNote", "Note", (
                  <Textarea
                    id={idFor("verificationNote")}
                    rows={2}
                    className="min-h-0 px-2 py-1 text-[12.5px] md:text-[12.5px]"
                    value={draft.verificationNote}
                    onChange={(e) => set("verificationNote")(e.target.value)}
                    disabled={busy}
                  />
                ))}
              </>
            )}

            {general}
            <div className="mt-1 flex items-center gap-2">
              {value && (
                <Button type="button" variant="ghost" size="sm" className="h-7 min-h-0 px-2 text-destructive" disabled={busy} onClick={() => { setRemoving(true); setError(null); }}>
                  Remove
                </Button>
              )}
              <div className="ml-auto flex gap-2">
                <Button type="button" variant="outline" size="sm" className="h-7 min-h-0" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" size="sm" className="h-7 min-h-0" disabled={busy}>
                  {busy && <Loader2 className="animate-spin" />}Save
                </Button>
              </div>
            </div>
          </form>
        )}
      </PopoverContent>
    </Popover>
  );
}
